import { useScrollPosition } from "@n8tb1t/use-scroll-position";
import classNames from "classnames";
import Head from "next/head";
import { FC, useEffect, useState } from "react";
import InfiniteScroll from "react-infinite-scroller";
import { useUpdateEffect } from "react-use";
import AdsIds from "../../../../data/ads";
import { ArticleWithTags } from "../../../../pages/nyheter/[slug]/[id]";
import { incrementViewCount } from "../../../../services/api/news";
import { API } from "../../../../services/api/types";
import { getClubName } from "../../../../utils/getClubName";
import { getRouteByEntityType } from "../../../../utils/getRouteByTagName";
import { shallowNavigate } from "../../../../utils/shallowNavigate";
import slugify from "../../../../utils/slugify";
import Advertisement from "../../../Advertisement";
import Article from "../../../Article";
import ArticleTag from "../../../Article/ArticleTag";
import ArticleTagItem from "../../../ArticleItem/ArticleTagItem";
import SecondaryGrid from "../../../Grid/SecondaryGrid";
import SectionHeader from "../../../SectionHeader";
import VideoPlayer from "../../../VideoPlayer";
import ArticleDivider from "../ArticleDivider";
import NextArticle from "../NextArticle";
import { getActiveArticleStatus } from "../utils/getActiveArticleStatus";
import YOffsetAnnouncer from "../YOffsetAnnouncer";

type ArticleFeedDesktopProps = {
  articles: ArticleWithTags[];
  loadMore: () => void;
};

const ArticleFeedDesktop: FC<ArticleFeedDesktopProps> = (props) => {
  const [articleIdToYOffsetMap, setArticleIdToYOffsetMap] = useState<{
    [key: number]: number;
  }>({});
  const [currentArticle, setCurrentArticle] = useState(
    props.articles[0].article
  );
  const [nextArticle, setNextArticle] = useState<API.Article | null>(
    props.articles[1]?.article
  );
  const [progress, setProgress] = useState(0);
  const firstArticle = props.articles[0].article;
  const isSponsored = firstArticle.categoriesId === 24;

  useScrollPosition(
    ({ currPos }) => {
      const {
        nextArticle: _nextArticle,
        currentArticle: _currentArticle,
        progress: _progress,
      } = getActiveArticleStatus(
        props.articles.map((a) => a.article),
        articleIdToYOffsetMap,
        Math.abs(currPos.y)
      );
      if (_currentArticle && _currentArticle.id !== currentArticle.id)
        setCurrentArticle(_currentArticle);

      if (_nextArticle && _nextArticle.id !== nextArticle?.id)
        setNextArticle(_nextArticle);

      if (_progress !== progress) setProgress(_progress);
    },
    [
      articleIdToYOffsetMap,
      props.articles,
      currentArticle,
      nextArticle,
      progress,
    ],
    undefined,
    false,
    50
  );

  useUpdateEffect(() => {
    const article = currentArticle || props.articles[0].article;
    const newRoute = `/nyheter/${slugify(article.title)}/${article.id}`;
    shallowNavigate(newRoute);
  }, [currentArticle]);

  useEffect(() => {
    incrementViewCount(currentArticle.id);
  }, [currentArticle]);

  const onAnnounceYOffset = (articleId: number) => (yOffset: number) => {
    setArticleIdToYOffsetMap((_articleIdToYOffsetMap) => ({
      ..._articleIdToYOffsetMap,
      [articleId]: yOffset,
    }));
  };

  const pageMeta = {
    title: currentArticle?.title || currentArticle?.titleShort,
    description: currentArticle?.preamble || currentArticle?.excerpt,
    image: `${process.env.NEXT_PUBLIC_ASSET_SERVER_URL}${currentArticle?.imageLarge}`,
  };

  return (
    <>
      <Head>
        <title>{`${pageMeta.title} - Fotbolltransfers.com`}</title>
        <meta property="og:title" content={pageMeta.title} />
        <meta
          name="description"
          content={`${pageMeta.description} - Fotbolltransfers.com`}
        />
        <meta property="og:image" content={pageMeta.image} />
      </Head>
      <InfiniteScroll
        threshold={
          typeof window !== "undefined" ? window!.innerHeight * 0.75 : 500
        }
        hasMore
        pageStart={0}
        loadMore={props.loadMore}
      >
        {props.articles.map((article, index) => {
          const isFirst = index === 0;
          const showNativeAd = index === 1;

          return (
            <div key={article.article.id}>
              {!isFirst && (
                <>
                  <div className={classNames("mt-5")} />
                  {!isSponsored && (
                    <>
                      {showNativeAd && (
                        <>
                          <Advertisement id={AdsIds.desktop_native} />
                          <div className={classNames("mb-5")} />
                        </>
                      )}
                      <Advertisement id={AdsIds.casino_list} />
                      <div className={classNames("mb-5")} />
                    </>
                  )}
                  <ArticleDivider />
                </>
              )}
              <SecondaryGrid
                left={
                  <YOffsetAnnouncer
                    onChange={onAnnounceYOffset(article.article.id)}
                  >
                    <Article
                      games={article.games}
                      campaign={article.campaign}
                      titlePosition="TOP"
                      article={article.article}
                      tags={article.tags}
                      adConfig={{
                        preamble: null,
                        social: null,
                        text: null,
                        videoIntelligence:
                          isFirst && !isSponsored
                            ? {
                                end: 2,
                                position: 2,
                                adProps: {
                                  id: AdsIds.desktop_vi,
                                },
                              }
                            : null,
                      }}
                    />
                  </YOffsetAnnouncer>
                }
                right={
                  <>
                    {isFirst && !isSponsored && !firstArticle.hideVideo && (
                      <div>
                        <SectionHeader label="Video" />
                        <div className="my-5 min-h-[291px]">
                          <VideoPlayer id="AAFqTWtkmWIsIgcc" />
                        </div>
                      </div>
                    )}

                    {article.featuredNews.length > 0 && (
                      <>
                        <SectionHeader label="Relaterade nyheter" />
                        {article.featuredNews.map((articleTag) => {
                          return (
                            <div
                              key={articleTag.id}
                              className={classNames(["py-5", "border-b"])}
                            >
                              <ArticleTagItem articleTag={articleTag} />
                            </div>
                          );
                        })}
                        <div className={classNames("mb-5")} />
                      </>
                    )}

                    {article.tags.filter((tag) => tag.type !== "league")
                      .length > 0 && (
                      <>
                        <SectionHeader label="Taggar" />
                        {article.tags
                          .filter(
                            (tag) => !["league", "tag"].includes(tag.type)
                          )
                          .map((tag, tagIndex) => {
                            const isEven = tagIndex % 2 === 0;
                            return (
                              <ArticleTag
                                key={tag.id}
                                href={getRouteByEntityType(
                                  tag.type as FixMeLater,
                                  slugify(tag.name),
                                  tag.id
                                )}
                                label={
                                  tag.type === "club"
                                    ? getClubName(tag.name)
                                    : tag.name
                                }
                                imageSrc={`${process.env.NEXT_PUBLIC_ASSET_SERVER_URL}${tag.image}`}
                                roundImage={[
                                  "player",
                                  "manager",
                                  "agent",
                                ].includes(tag.type)}
                                darkBackground={!isEven}
                                id={tag.id}
                                type={tag.type}
                              />
                            );
                          })}
                        {isFirst && (
                          <div className={classNames(["mt-5"])}>
                            <Advertisement id={AdsIds.tws_insider} />
                          </div>
                        )}
                      </>
                    )}
                  </>
                }
              />
            </div>
          );
        })}
      </InfiniteScroll>
      {nextArticle && (
        <NextArticle
          isVisible={progress > 0.3 && progress < 0.95}
          yOffset={articleIdToYOffsetMap[nextArticle.id]}
          article={nextArticle}
          progress={progress}
        />
      )}
    </>
  );
};

export default ArticleFeedDesktop;
